import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { RightClickDropdown } from 'components/RightClickDropdown';
import DataRoomNodeTagAndVersion from 'components/DataRoomNodeTagAndVersion';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import utils, { displayDataRoomNodeSize, getNodeMimeType } from 'utils';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useMatchMedia from 'hooks/useMatchMedia';
import usePastTime from 'hooks/usePastTime';
import useDataRoomDocument from 'hooks/useDataRoomDocument';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import DotshorizontalIcon from 'icons/Dotshorizontal.icon';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { ALIGNMENT } from 'models/Global';
import useMobileLongClick from 'hooks/useMobileLongClick';
import ShareableLinkDocumentMobileModal from '../ShareableLinkDocumentMobileModal';
import ShareableLinkNodePreview from '../ShareableLinkNodePreview';

import './ShareableLinkDocument.styles.scss';
import './ShareableLinkDocument.styles.responsive.scss';

const NAME_ID = 'name';

type ShareableLinkDocumentProps = {
  className?: string;
  node: DataRoomNode;
  dropdownOptions?: any[];
  baseDocsPath: string;
  isSelected?: boolean;
  isSelectedMode?: boolean;
  shareableLinkId: string;
  dataRoomName: string;
  onSelect?: (node: DataRoomNode) => void;
};

const ShareableLinkDocument: React.FC<ShareableLinkDocumentProps> = (props) => {
  const {
    className,
    node,
    isSelected,
    isSelectedMode,
    baseDocsPath,
    shareableLinkId,
    dataRoomName,
    dropdownOptions = [],
    onSelect,
  } = props;

  const classes = classNames(
    'shareable-link-document',
    { 'selected-mode': isSelectedMode, selected: isSelected },
    className,
  );

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const [phoneModal, phoneModalRef] = useCallbackRef<ModalRef>(null);
  const [previewModal, previewModalRef] = useCallbackRef<ModalRef>(null);

  const isPhablet = useMatchMedia('isPhablet');

  const pastTime = usePastTime();
  const navigate = useNavigate();

  const { _id, name, tag, version, modifiedAt } = node;

  const { nodeSize, canDownload, isFolder } = useDataRoomDocument(node);

  const { handleTouchEnd, handleTouchStart } = useMobileLongClick({
    onSelect: () => onSelect(node),
  });

  const showMenu = useMemo(
    () => !!dropdownOptions?.length,
    [dropdownOptions?.length],
  );

  const rightClickDisabled = !showMenu || dropdown?.isOpen;

  const showMobileOptions = canDownload;

  const handleOpenFile = useCallback(
    () => previewModal?.open(),
    [previewModal],
  );

  const handleOpenFolder = useCallback(
    () => navigate(`${baseDocsPath}${_id}`),
    [baseDocsPath, _id, navigate],
  );

  const handleOpen = isFolder ? handleOpenFolder : handleOpenFile;

  const handleOnClick = useCallback(
    (e) => {
      if (isPhablet) {
        if (isSelectedMode) {
          onSelect?.(node);
        } else {
          handleOpen();
        }

        return;
      }

      const isNameClicked = e.target.id === NAME_ID;

      if (isNameClicked) {
        handleOpen();
      } else {
        onSelect?.(node);
      }
    },
    [isPhablet, isSelectedMode, node, onSelect, handleOpen],
  );

  const RightSideMenu = useMemo(
    () => (
      <span className="right-menu">
        <span>{displayDataRoomNodeSize(nodeSize)}</span>

        <span>{pastTime(modifiedAt)}</span>

        {showMobileOptions && !isSelectedMode && (
          <span className="mobile-menu-open">
            <Button onClick={phoneModal?.open} icon={DotshorizontalIcon} />
          </span>
        )}

        {showMenu ? (
          <ThreeDotsDropdown
            ref={dropdownRef}
            options={dropdownOptions}
            className="dropdown-on-docs"
            withBorder={false}
            alignment={ALIGNMENT.VERTICAL}
            renderAsPortal
          />
        ) : (
          <span style={{ width: '24px' }} />
        )}
      </span>
    ),
    [
      nodeSize,
      modifiedAt,
      showMobileOptions,
      isSelectedMode,
      showMenu,
      dropdownOptions,
      phoneModal,
      dropdownRef,
      pastTime,
    ],
  );

  return (
    <RightClickDropdown options={dropdownOptions} disabled={rightClickDisabled}>
      <div className={classes}>
        <Button
          onClick={handleOnClick}
          {...(isPhablet && {
            onTouchStart: handleTouchStart,
            onTouchEnd: handleTouchEnd,
            onTouchMove: handleTouchEnd,
          })}
        >
          {isPhablet && isSelectedMode ? (
            <div className="checkbox-wrapper">
              <Checkbox
                onChange={() => onSelect?.(node)}
                checked={isSelected}
              />
            </div>
          ) : (
            <MymeTypeIcon mimeType={getNodeMimeType(node)} />
          )}

          <div className="node-info">
            <div>
              <span id={NAME_ID}>{name}</span>
              <DataRoomNodeTagAndVersion tag={tag} version={version} />
            </div>

            <span className="only-show-on-mobile">
              {!!nodeSize && <span>{utils.humanizeFileSize(nodeSize)}</span>}

              <span>{pastTime(modifiedAt)}</span>
            </span>
          </div>
        </Button>

        {RightSideMenu}

        <ShareableLinkDocumentMobileModal
          ref={phoneModalRef}
          node={node}
          options={dropdownOptions}
        />

        <ShareableLinkNodePreview
          ref={previewModalRef}
          node={node}
          shareableLinkId={shareableLinkId}
          dataRoomName={dataRoomName}
        />
      </div>
    </RightClickDropdown>
  );
};

export default ShareableLinkDocument;
